import React, { useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import type { LucideIcon } from "lucide-react";
import { ChevronRight, ChevronDown } from "lucide-react";

import { cn } from "@/lib/utils";

export type SidebarTreeItem = {
  id: string;
  label: string;
  icon?: LucideIcon;
  to?: string;
  children?: SidebarTreeItem[];
};

type SidebarTreeProps = {
  items: SidebarTreeItem[];
  onNavigate?: () => void;
};

const isTreePath = (path: string, items: SidebarTreeItem[]): boolean => {
  for (const item of items) {
    if (item.to === path) return true;
    if (item.children && isTreePath(path, item.children)) return true;
  }
  return false;
};

const TreeItem = ({
  item,
  onNavigate,
  currentPath,
}: {
  item: SidebarTreeItem;
  onNavigate?: () => void;
  currentPath: string;
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const location = useLocation();
  const hasChildren = item.children && item.children.length > 0;
  const hasLink = item.to && !hasChildren;
  const isActive = item.to && location.pathname === item.to;

  React.useEffect(() => {
    if (hasChildren && item.children) {
      const hasActiveChild = item.children.some(
        (child) =>
          child.to === currentPath ||
          (child.children && isTreePath(currentPath, child.children)),
      );
      if (hasActiveChild) {
        setIsExpanded(true);
      }
    }
  }, [currentPath, hasChildren, item.children]);

  const toggleExpanded = () => {
    if (hasChildren) {
      setIsExpanded(!isExpanded);
    }
  };

  const content = (
    <div
      className={cn(
        "relative flex items-center gap-3 rounded-lg px-4 py-2.5 text-sm font-normal text-neutral-700 transition-colors duration-normal ease-standard hover:bg-primary-50 hover:text-primary-500",
        isActive && "bg-primary-50 text-primary-500 font-medium",
        hasChildren && "cursor-pointer",
        !item.icon && "pl-[1.70rem]",
      )}
      onClick={() => {
        if (hasChildren) {
          toggleExpanded();
        }
      }}
    >
      {isActive && (
        <span
          aria-hidden="true"
          className="absolute left-1 top-1/2 h-6 w-0 -translate-y-1/2 rounded-full bg-transparent transition-all duration-normal ease-standard"
        />
      )}

      {item.icon && (
        <item.icon
          className={cn(
            "h-5 w-5 text-neutral-600 transition-colors duration-normal ease-standard",
            isActive && "text-primary-600",
          )}
        />
      )}

      <span className="flex-1">{item.label}</span>

      {hasChildren && (
        <span className="ml-auto">
          {isExpanded ? (
            <ChevronDown className="h-4 w-4 text-neutral-500" />
          ) : (
            <ChevronRight className="h-4 w-4 text-neutral-500" />
          )}
        </span>
      )}
    </div>
  );

  return (
    <div className="relative">
      {hasLink ? (
        <NavLink to={item.to!} onClick={onNavigate}>
          {content}
        </NavLink>
      ) : (
        content
      )}

      {hasChildren && isExpanded && (
        <div className="relative ml-6 mt-0.5 flex flex-col gap-1 border-l border-neutral-200 pl-3">
          {item.children?.map((child) => (
            <TreeItem
              key={child.id}
              item={child}
              onNavigate={onNavigate}
              currentPath={currentPath}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export const SidebarTree = ({ items, onNavigate }: SidebarTreeProps) => {
  const location = useLocation();
  const currentPath = location.pathname;

  return (
    <div className="flex flex-col gap-1">
      {items.map((item) => (
        <TreeItem
          key={item.id}
          item={item}
          onNavigate={onNavigate}
          currentPath={currentPath}
        />
      ))}
    </div>
  );
};
